import { useEffect, useState, useMemo } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Save, Printer, Calculator, Printer as PrinterIcon } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const now = new Date();
const COLS = ["basic", "hra", "other_allowances", "pf", "esi", "tds", "other_deductions"];

export default function Payroll() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [dept, setDept] = useState("all");
  const [emps, setEmps] = useState([]);
  const [depts, setDepts] = useState([]);
  const [rows, setRows] = useState({}); // { emp_id: {basic, hra, ...} }

  const load = async () => {
    try {
      const [e, d, p] = await Promise.all([api.get("/employees"), api.get("/departments"), api.get(`/payroll?month=${month}&year=${year}`)]);
      const active = e.data.filter(x => x.status !== "Inactive");
      setEmps(active); setDepts(d.data);
      const m = {}; active.forEach(x => { m[x.id] = { basic: x.basic || 0, hra: x.hra || 0, other_allowances: x.other_allowances || 0, pf: 0, esi: 0, tds: 0, other_deductions: 0 }; });
      p.data.forEach(r => { m[r.employee_id] = { basic: r.basic, hra: r.hra, other_allowances: r.other_allowances, pf: r.pf, esi: r.esi, tds: r.tds, other_deductions: r.other_deductions }; });
      setRows(m);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [month, year]); // eslint-disable-line

  const set = (eid, k, v) => setRows(r => ({ ...r, [eid]: { ...r[eid], [k]: Number(v) || 0 } }));
  const calc = (r) => { const g = (r.basic || 0) + (r.hra || 0) + (r.other_allowances || 0); const n = g - ((r.pf || 0) + (r.esi || 0) + (r.tds || 0) + (r.other_deductions || 0)); return { gross: g, net: n }; };
  const filtered = useMemo(() => emps.filter(e => dept === "all" || e.department === dept), [emps, dept]);

  const applyCalc = () => setRows(r => ({ ...r })); // trigger re-render (values already computed live)
  const save = async () => {
    try {
      await Promise.all(filtered.map(e => api.post("/payroll", { month, year, employee_id: e.id, ...rows[e.id] })));
      toast.success(`Payroll saved for ${month.toString().padStart(2, "0")}/${year}`);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const printSlip = (eid) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/salary-slip/${month}/${year}/${eid}/pdf?_=${Date.now()}`, "_blank");

  const pg = usePagination(filtered);
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div><div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Wallet className="w-3 h-3"/> HRMS</div><h1 className="font-display font-black text-4xl uppercase tracking-tighter">Payroll</h1></div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger data-testid="pay-month" className="mt-1 rounded-sm bg-secondary border-border w-32"><SelectValue/></SelectTrigger>
              <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map(m => <SelectItem key={m} value={String(m)}>{new Date(2000, m - 1).toLocaleString("en", { month: "long" })}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger data-testid="pay-year" className="mt-1 rounded-sm bg-secondary border-border w-24"><SelectValue/></SelectTrigger>
              <SelectContent>{[year - 1, year, year + 1].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Department</Label>
            <Select value={dept} onValueChange={setDept}><SelectTrigger data-testid="pay-dept" className="mt-1 rounded-sm bg-secondary border-border w-40"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="all">All Departments</SelectItem>{depts.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="pay-calc" onClick={applyCalc} variant="outline" className="rounded-sm text-xs uppercase tracking-wider"><Calculator className="w-3 h-3 mr-1"/>Calculate</Button>
          <Button data-testid="pay-save" onClick={save} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider"><Save className="w-3 h-3 mr-1"/>Save</Button>
          <Button variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/>Print</Button>
        </div>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-auto">
        <table className="w-full text-xs" data-testid="payroll-grid">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.15em] text-muted-foreground"><tr>
            <th className="text-left p-2">Employee</th>{["Basic", "HRA", "Other Allow.", "Gross", "PF", "ESI", "TDS", "Other Ded.", "Net"].map(h => <th key={h} className="text-right p-2 min-w-24">{h}</th>)}<th className="p-2 w-16 text-right">Slip</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">No employees</td></tr>}
            {pg.pageItems.map(e => { const r = rows[e.id] || {}; const { gross, net } = calc(r); return (
              <tr key={e.id} data-testid={`pay-row-${e.id}`} className="border-t border-border">
                <td className="p-2 font-semibold whitespace-nowrap">{e.name}</td>
                {COLS.slice(0, 3).map(k => <td key={k} className="p-1"><Input data-testid={`pay-${k}-${e.id}`} type="number" value={r[k] ?? 0} onChange={(ev) => set(e.id, k, ev.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>)}
                <td className="p-2 text-right font-mono text-emerald-500" data-testid={`pay-gross-${e.id}`}>{inr(gross)}</td>
                {COLS.slice(3).map(k => <td key={k} className="p-1"><Input data-testid={`pay-${k}-${e.id}`} type="number" value={r[k] ?? 0} onChange={(ev) => set(e.id, k, ev.target.value)} className="h-8 rounded-sm bg-secondary border-border text-xs text-right"/></td>)}
                <td className="p-2 text-right font-mono font-bold text-primary" data-testid={`pay-net-${e.id}`}>{inr(net)}</td>
                <td className="p-2 text-right"><Button size="sm" variant="ghost" data-testid={`pay-print-${e.id}`} onClick={() => printSlip(e.id)} className="h-7 w-7 p-0"><PrinterIcon className="w-3 h-3"/></Button></td>
              </tr>
            ); })}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
